import axios, { AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextField from '@pelckmans/elementis/components/text-field';
import UploadField from '@pelckmans/elementis/components/upload-field';

import { TokenAccessor } from '../../../types/API';
import { BasePayload } from '../api';
import BaseAddMaterialModal, {
  BaseAddMaterialModalBaseProps,
  BaseFormValues,
  RenderProps,
} from '../BaseAddMaterialModal';
import { ErrorResponseData, FILE_UPLOAD_ERROR_RESPONSES, getAWSUploadFields, uploadFile } from './api';
import { FileUserMaterial } from '../../../../../api/user-material';
import { useDefaultToast } from '../../../../../hooks/useDefaultToast';

interface Props extends BaseAddMaterialModalBaseProps {
  id: string;
  material?: FileUserMaterial;
}

export interface AddFileMaterialPayload extends BasePayload {
  file: string;
}

export interface EditFileMaterialPayload extends BasePayload {
  file?: string;
}

export interface FileMaterial extends BaseFormValues {
  file: string;
  fileId: string;
}

const FORM_ID = 'add-file-form';
const BASE = 'SHARED_COMPONENTS.ADD_MATERIAL_MODAL';

export default function AddFileMaterialModal({
  id,
  tokenAccessor,
  nodeId,
  moduleId,
  studioGroup,
  onSubmit: onSubmitCallback,
  material,
  extraPayload,
}: Props) {
  function mapToPayload(data: FileMaterial): AddFileMaterialPayload | EditFileMaterialPayload | null {
    if (material) {
      if (material.name === data.name && material.file.id === data.fileId && material.file.originalName === data.file) {
        return null;
      }

      return {
        name: data.name,
        ...(material.file.originalName !== data.file || material.file.id !== data.fileId ? { file: data.fileId } : {}),
      };
    } else {
      return {
        name: data.name,
        file: data.fileId,
        maxScore: 1,
      };
    }
  }

  function getDefaultValues(material?: FileUserMaterial): FileMaterial {
    return {
      name: material?.name || '',
      file: material?.file.originalName || '',
      fileId: material?.file.id || '',
    };
  }

  return (
    <BaseAddMaterialModal
      modalId={id}
      formId={FORM_ID}
      tokenAccessor={tokenAccessor}
      defaultValues={getDefaultValues(material)}
      nodeId={nodeId}
      moduleId={moduleId}
      studioGroup={studioGroup}
      onSubmit={onSubmitCallback}
      payloadMapper={mapToPayload}
      materialId={material?.id}
      extraPayload={extraPayload}
    >
      {props => <FileForm {...props} tokenAccessor={tokenAccessor} studioGroup={studioGroup} material={material} />}
    </BaseAddMaterialModal>
  );
}

function FileForm({
  register,
  onSubmit,
  formState: { errors },
  setValue,
  clearErrors,
  tokenAccessor,
  setError,
  studioGroup,
  material,
}: RenderProps<FileMaterial> & { tokenAccessor: TokenAccessor; studioGroup: string; material?: FileUserMaterial }) {
  const { t } = useTranslation();
  const { failedToast } = useDefaultToast();
  const [progress, setProgress] = useState(0);
  const [showProgress, setShowProgress] = useState(false);
  const [currentRequest, setCurrentRequest] = useState<AbortController | null>(null);
  const [fileName, setFileName] = useState<string>();

  useEffect(() => {
    register('fileId', { required: t(`${BASE}.FILE_INPUT.EMPTY`) });

    if (material) {
      setFileName(material.file.originalName);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onFileSelected(file: File) {
    setValue('file', file.name);
    clearErrors('file');

    setProgress(0);
    setFileName(file.name);
    setShowProgress(true);

    try {
      const accessToken = await tokenAccessor.tokenGetter();
      const uploadData = await getAWSUploadFields(accessToken, file, studioGroup);

      setValue('fileId', uploadData.fileId);

      const abortController = new AbortController();
      setCurrentRequest(abortController);

      const axiosConfig: AxiosRequestConfig = {
        onUploadProgress: function (progressEvent) {
          // Upload percentage max 98% for catching buffer bloat issue (depending on user hardware)
          const percentCompleted = Math.min(
            98,
            Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100)),
          );
          setProgress(percentCompleted);
        },
        signal: abortController.signal,
        skipAuthorization: true,
      };

      await uploadFile(uploadData.postData, file, axiosConfig);

      setProgress(100);
      clearErrors('fileId');
      clearErrors('file');
    } catch (error) {
      setShowProgress(false);
      setProgress(0);

      if (axios.isCancel(error)) return;

      if (error instanceof Error) {
        failedToast();
        return;
      }

      const { message, statusCode, details } = error as ErrorResponseData;
      switch (statusCode) {
        case 400: {
          if (message === FILE_UPLOAD_ERROR_RESPONSES.FILE_SIZE_EXCEEDED) {
            const maxSizeInMb = Math.floor((details.maxFileSize || 0) / 1024 / 1024);
            setError('file', {
              type: 'manual',
              message: t(`${BASE}.FILE_INPUT.FILE_SIZE_EXCEEDED`, { size: maxSizeInMb }),
            });
          } else if (message === FILE_UPLOAD_ERROR_RESPONSES.EXTENSION_NOT_ACCEPTED) {
            setError('file', {
              type: 'manual',
              message: t(`${BASE}.FILE_INPUT.FILE_NOT_SUPPORTED`),
            });
          }
          break;
        }
        case 409: {
          setError('file', {
            type: 'manual',
            message: t(`${BASE}.FILE_INPUT.STORAGE_LIMIT_EXCEEDED`),
          });
          break;
        }
        default:
          failedToast();
      }
    } finally {
      setCurrentRequest(null);
    }
  }

  function onClear() {
    if (currentRequest) {
      currentRequest.abort();
      setCurrentRequest(null);
    } else {
      setValue('file', '');
    }
    setFileName(undefined);
    setValue('fileId', '');

    setShowProgress(false);
    setProgress(0);

    clearErrors('fileId');
    clearErrors('file');
  }

  return (
    <form id={FORM_ID} name={FORM_ID} onSubmit={onSubmit} className="form-fields" noValidate>
      <TextField
        id="name"
        key="name"
        labelText={t(`${BASE}.NAME_INPUT.LABEL`)}
        autoComplete="on"
        required
        error={!!errors.name}
        errorText={errors.name?.message}
        {...register('name', {
          required: t(`${BASE}.NAME_INPUT.EMPTY`),
        })}
      />
      <UploadField
        id="file"
        key="file"
        labelText={t(`${BASE}.FILE_INPUT.LABEL`)}
        onFileSelected={onFileSelected}
        onClear={onClear}
        required
        helpText={t(`${BASE}.FILE_INPUT.HELPER_TEXT`)}
        error={!!errors.file || !!errors.fileId}
        errorText={errors.file?.message || errors.fileId?.message}
        showProgress={showProgress}
        progress={progress}
        value={fileName || ''}
        {...register('file', {
          required: t(`${BASE}.FILE_INPUT.EMPTY`),
          validate: () => {
            if (showProgress && progress < 100) {
              return t(`${BASE}.FILE_INPUT.IN_PROGRESS`);
            }
          },
        })}
      />
    </form>
  );
}
